/** レポートデータ更新画面: データ更新の確認モーダル（SR-039）。実行処理は onExecute を呼び出し元で行う。 */
'use strict'

import * as React from 'react'
import { useStore } from 'react-redux'
import { useModal, useModalExtra } from '../../../../_components/Modal'
import { useModifyClassList } from '../../../../common/utils/CSSUtils'
import { useReportDataUpdateStore } from '../extra/store'
import { validateDataUpdatePreconditions } from '../extra/validation'

/**
 * @param {{
 *   onExecute: (args: { userSub: string; periodKind: string; tableKey: string | number }) => Promise<void>
 *   onPageError?: (message: string) => void
 *   globalReduxStore?: import('redux').Store
 * }} options
 *   ReportDataUpdateProvider 配下では useStore がローカルストアのため、親からアプリ全体の Store を渡す
 * @returns {() => Promise<void>}
 */
export function useDataUpdateConfirmModal(options) {
  const { onExecute, onPageError, globalReduxStore: globalReduxStoreOpt } = options ?? {}
  const showModal = useModal()
  const { _closeUpModal } = useModalExtra()
  const { promisify } = useModifyClassList('.modal-window', ['w-[600px]'])
  const localStore = useStore()
  const reportStore = useReportDataUpdateStore()
  const globalReduxStore = globalReduxStoreOpt ?? localStore

  return React.useCallback(() => {
    return promisify((res) => {
      showModal({
        title: 'データ更新の確認',
        description: (
          <div className="text-body grey--text text-center px-2">
            <p>レポートデータの更新を開始します。よろしいですか？</p>
          </div>
        ),
        isWide: false,
        isCancel: true,
        cancelName: 'キャンセル',
        onClose: res,
        button: [
          {
            name: '実行',
            className: 'footer-btn button_base',
            close: false,
            click: async () => {
              try {
                const rs = reportStore.getState()
                const pre = validateDataUpdatePreconditions({
                  statusByDataKind: rs.statusByDataKind,
                  dataTaskStatus: rs.dataCreateTask?.dataTaskStatus,
                  dataFixStatus: rs.dataCreateTask?.dataFixStatus
                })
                if (!pre.ok) {
                  onPageError?.(pre.message ?? '')
                  return
                }
                const taskKey = rs.dataCreateTaskKey
                const periodKind = String(rs.dataCreateTask?.periodType ?? rs.periodTypeFromUrl ?? '')
                const gst = globalReduxStore.getState()
                const userSub = String(gst.user?.sub ?? '')
                if (!userSub || taskKey === null || taskKey === undefined) {
                  onPageError?.('処理を実行できません。')
                  return
                }
                try {
                  await onExecute?.({ userSub, periodKind, tableKey: taskKey })
                } catch (err) {
                  console.error(err)
                  onPageError?.('レポートデータ更新処理でエラーが発生しました。')
                }
              } finally {
                _closeUpModal()
                res()
              }
            }
          }
        ]
      })
    })
  }, [_closeUpModal, globalReduxStore, onExecute, onPageError, promisify, reportStore, showModal])
}
